// Reusable attachment list + uploader used in both the agent ticket
// detail and the customer-facing portal ticket detail. The caller decides:
//   - apiBase     ('/service-desk' for agent, '/public/portal/:slug' for
//                  anonymous, '/customer' for logged-in customers)
//   - uploadUrl   full POST URL
//   - listUrl     full GET URL
//   - deleteUrl   optional — if provided each row has a delete button
//   - canUploadInternal  bool — show the "Internal" toggle (agent only)
//   - identityExtra (object) — extra form fields appended (e.g.,
//                    requester_email for anonymous uploads)
// Kept dumb on purpose: no business logic, just file IO.
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Paperclip, DownloadSimple, X, LockSimple, Upload } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

function bytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ServiceDeskAttachments({
  listUrl, uploadUrl, deleteUrl, canUploadInternal = false,
  identityExtra = null, withCredentials = true, headers = {}, readOnly = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [internal, setInternal] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(listUrl, { withCredentials, headers });
      setItems(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // `headers`/`withCredentials` are read from the latest closure. They are
    // intentionally NOT in the deps: parents often pass a fresh `headers={}`
    // object literal each render, which would otherwise change `load` on every
    // render and cause an infinite re-fetch loop (flicker / stuck "Loading…").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (canUploadInternal) fd.append('is_internal', String(internal));
    if (identityExtra) {
      Object.entries(identityExtra).forEach(([k, v]) => { if (v !== undefined && v !== null) fd.append(k, String(v)); });
    }
    try {
      await axios.post(uploadUrl, fd, { withCredentials, headers });
      await load();
      toast.success('File attached');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const del = async (attachmentId) => {
    if (!deleteUrl) return;
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await axios.delete(deleteUrl(attachmentId), { withCredentials, headers });
      setItems(prev => prev.filter(a => a.attachment_id !== attachmentId));
      toast.success('Removed');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-2" data-testid="sd-attachments">
      {loading ? (
        <p className="text-xs text-zinc-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-zinc-400 italic">No attachments yet.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map(a => (
            <div key={a.attachment_id} className="flex items-center gap-2 p-2 border border-zinc-200 rounded bg-zinc-50 text-sm"
              data-testid={`sd-att-row-${a.attachment_id}`}>
              <Paperclip size={14} className="text-zinc-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-zinc-800">{a.original_filename}</p>
                <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                  {bytes(a.size_bytes)}
                  {a.is_internal && <span className="flex items-center gap-0.5 text-amber-700"><LockSimple size={9} weight="fill" /> Internal</span>}
                </p>
              </div>
              <a href={`${API_BASE}/sd-attachments/${a.attachment_id}/download?download=1`}
                className="text-zinc-500 hover:text-zinc-900 p-1" title="Download"
                data-testid={`sd-att-download-${a.attachment_id}`}>
                <DownloadSimple size={14} />
              </a>
              {deleteUrl && (
                <button onClick={() => del(a.attachment_id)} className="text-zinc-400 hover:text-red-600 p-1" title="Remove"
                  data-testid={`sd-att-delete-${a.attachment_id}`}>
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center gap-2 pt-1">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0])}
            data-testid="sd-att-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="sd-att-upload-btn">
            <Upload size={13} className="mr-1.5" />
            {uploading ? 'Uploading…' : 'Attach file'}
          </Button>
          {canUploadInternal && (
            <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer select-none">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)}
                data-testid="sd-att-internal-toggle" />
              Internal (agents only)
            </label>
          )}
        </div>
      )}
    </div>
  );
}
